// Thin wrapper around a WebSocket that handles JSON encoding, message
// dispatch, and automatic reconnection on close.
//
// Reconnection rationale: Cloud Run drops WebSocket sessions at 60 minutes.
// Without auto-reconnect, players get punted mid-game. The reconnect uses
// exponential backoff capped at 5 s, gives up after MAX_RETRIES, and emits
// onReconnecting/onReconnected so the UI can show status.

import {
  decodeS2C,
  encodeMessage,
  PROTOCOL_VERSION,
  type BotPersonality,
  type C2SInput,
  type C2SMessage,
  type ChatScope,
  type S2CMessage,
  type S2CSnapshot,
  type S2CWelcome,
} from '@bolo2k/shared';

export interface WebSocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  addEventListener?(type: string, listener: (ev: MessageEvent | Event) => void): void;
  on?(type: string, listener: (...args: unknown[]) => void): void;
}

export type WebSocketFactory = (url: string) => WebSocketLike;

export interface ConnectionEvents {
  onWelcome?: (welcome: S2CWelcome) => void;
  onSnapshot?: (snap: S2CSnapshot) => void;
  onMessage?: (msg: S2CMessage) => void;
  onClose?: (code: number, reason: string) => void;
  onError?: (err: Error) => void;
  onReconnecting?: (attempt: number, delayMs: number) => void;
  onReconnected?: () => void;
  onGiveUp?: () => void;
}

export interface ConnectionOptions {
  /** Total reconnect attempts before giving up. Default 6 → ~31 s of trying. */
  maxRetries?: number;
  /** Cap on backoff delay between attempts. Default 5_000 ms. */
  maxBackoffMs?: number;
  /** Disable auto-reconnect entirely (used by tests). */
  noReconnect?: boolean;
  /** Display name to send via c2s.hello after each welcome (incl. reconnect). */
  name?: string;
}

export class Connection {
  private ws: WebSocketLike | null = null;
  private buildSeq = 0;
  private baseUrl: string;
  private events: ConnectionEvents;
  private factory: WebSocketFactory;
  private opts: Required<ConnectionOptions>;
  private retryCount = 0;
  private closedByCaller = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    url: string,
    events: ConnectionEvents = {},
    factory?: WebSocketFactory,
    opts: ConnectionOptions = {},
  ) {
    this.baseUrl = url;
    this.events = events;
    this.factory = factory ?? defaultFactory;
    this.opts = {
      maxRetries: opts.maxRetries ?? 6,
      maxBackoffMs: opts.maxBackoffMs ?? 5_000,
      noReconnect: opts.noReconnect ?? false,
      name: opts.name ?? '',
    };
    this.connect();
  }

  // Build the URL for the next connection attempt. Appends `&name=<x>` so
  // the server can match this connection against any pending-rejoin slot
  // with the same callsign — rejoin identity is the player's callsign, the
  // same key the persistent per-callsign stats use. No client-side storage
  // needed; the callsign comes from opts.name (lobby form input).
  private buildUrl(): string {
    if (!this.opts.name) return this.baseUrl;
    const sep = this.baseUrl.includes('?') ? '&' : '?';
    return `${this.baseUrl}${sep}name=${encodeURIComponent(this.opts.name)}`;
  }

  private sendHello(): void {
    this.send({
      type: 'c2s.hello',
      protocolVersion: PROTOCOL_VERSION,
      ...(this.opts.name ? { name: this.opts.name } : {}),
    });
  }

  sendInput(keys: number, build?: { action: number; targetX: number; targetY: number }): void {
    const msg: C2SInput = { type: 'c2s.input', keys };
    if (build) {
      this.buildSeq++;
      msg.build = { ...build, seq: this.buildSeq };
    }
    this.send(msg);
  }

  leave(): void {
    this.send({ type: 'c2s.leave' });
  }

  addBot(personality: BotPersonality): void {
    this.send({ type: 'c2s.addBot', personality });
  }

  removeBot(tankId: number): void {
    this.send({ type: 'c2s.removeBot', tankId });
  }

  requestAlly(target: number): void {
    this.send({ type: 'c2s.requestAlly', target });
  }

  cancelAlly(target: number): void {
    this.send({ type: 'c2s.cancelAlly', target });
  }

  sendChat(text: string, scope: ChatScope): void {
    this.send({ type: 'c2s.chat', text, scope });
  }

  setView(pillId: number): void {
    this.send({ type: 'c2s.setView', pillId });
  }

  close(): void {
    this.closedByCaller = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close(1000, 'client closing');
  }

  private send(msg: C2SMessage): void {
    if (!this.ws || this.ws.readyState !== 1) return; // OPEN === 1
    this.ws.send(encodeMessage(msg));
  }

  private connect(): void {
    const ws = this.factory(this.buildUrl());
    this.ws = ws;
    this.attach(ws);
  }

  private scheduleReconnect(): void {
    if (this.opts.noReconnect || this.closedByCaller) return;
    if (this.retryCount >= this.opts.maxRetries) {
      this.events.onGiveUp?.();
      return;
    }
    this.retryCount++;
    const delay = Math.min(this.opts.maxBackoffMs, 500 * 2 ** (this.retryCount - 1));
    this.events.onReconnecting?.(this.retryCount, delay);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  private attach(ws: WebSocketLike): void {
    const onMessage = (raw: unknown) => {
      const text =
        typeof raw === 'string'
          ? raw
          : raw && typeof (raw as { data?: unknown }).data === 'string'
            ? (raw as { data: string }).data
            : raw && typeof (raw as { toString: () => string }).toString === 'function'
              ? (raw as { toString: () => string }).toString()
              : '';
      const msg = decodeS2C(text);
      if (!msg) return;
      this.events.onMessage?.(msg);
      if (msg.type === 's2c.welcome') {
        if (this.retryCount > 0) this.events.onReconnected?.();
        this.retryCount = 0;
        // Server already assigned the slot on attach; hello carries our
        // chosen display name so it shows up in chat/alliance UI.
        this.sendHello();
        this.events.onWelcome?.(msg);
      } else if (msg.type === 's2c.snapshot') {
        this.events.onSnapshot?.(msg);
      }
    };

    const onClose = (code: number, reason: string) => {
      this.events.onClose?.(code, reason);
      // Don't retry on a clean close initiated by the client.
      if (code !== 1000) this.scheduleReconnect();
    };

    if (typeof ws.on === 'function') {
      ws.on('message', (data: unknown) => onMessage(data));
      ws.on('close', (code: unknown, reason: unknown) =>
        onClose(typeof code === 'number' ? code : 1000, String(reason ?? '')),
      );
      ws.on('error', (err: unknown) => this.events.onError?.(err instanceof Error ? err : new Error(String(err))));
    } else if (typeof ws.addEventListener === 'function') {
      ws.addEventListener('message', (ev) => onMessage((ev as MessageEvent).data));
      ws.addEventListener('close', (ev) => {
        const ce = ev as unknown as { code: number; reason: string };
        onClose(ce.code, ce.reason);
      });
      ws.addEventListener('error', () => this.events.onError?.(new Error('websocket error')));
    }
  }
}

function defaultFactory(url: string): WebSocketLike {
  if (typeof WebSocket === 'undefined') {
    throw new Error('global WebSocket is not available; pass a factory for non-browser environments');
  }
  return new WebSocket(url) as unknown as WebSocketLike;
}
